import React, { FC, useState } from "react";
import {
    closestCenter,
    DndContext,
    DragEndEvent,
    DragStartEvent,
    Modifier,
    TouchSensor,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import {
    arrayMove,
    horizontalListSortingStrategy,
    SortableContext,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CustomPointerSensor } from "./CustomPointerSensor";
import { SortableItem } from "./SortableItem";
import { IDispatchSetStateAction } from "./types";
import "@Styles/components/sortableList.scss";

export type ISortableItemProps = {
    DragHandler?: FC;
    className?: string;
    id: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
} & Record<string, any>;

export interface IItemRenderProps {
    index: number;
    item: ISortableItemProps;
}

export interface IChildrenProps {
    items: ISortableItemProps[];
}
export interface ISortableListProps<T extends ISortableItemProps> {
    /** Array of items to be sorted */
    items: T[];
    /** State setter function for items array */
    setItems: IDispatchSetStateAction<T[]>;
    /** Optional custom render function for each item */
    itemRender?: ({ item, index }: { item: T; index: number }) => JSX.Element;
    /** Optional custom render function for the entire list */
    children?: ({ items }: { items: T[] }) => JSX.Element;
    /** Whether to enable horizontal sorting (default: false) */
    horizontal?: boolean;
}


/**
 * A sortable list component that supports both horizontal and vertical sorting.
 * Implements drag and drop functionality using dnd-kit.
 */
export function SortableList<T extends ISortableItemProps>(props: ISortableListProps<T>) {
    const [activeId, setActiveId] = useState<string>("");
    const { items, setItems, children, itemRender, horizontal } = props;

    const sensors = useSensors(
        useSensor(CustomPointerSensor, {
            activationConstraint: { distance: 10 },
        }),
        useSensor(TouchSensor, {
            activationConstraint: { distance: 10 },
        })
    );

    /**
     * Restricts movement of draggable elements based on orientation
     */
    const restrictMovement: Modifier = ({ transform }) => {
        return {
            ...transform,
            x: horizontal ? transform.x : 0,
        };
    };

    /**
     * Handles the start of a drag operation
     */
    function handleDragStart({ active }: DragStartEvent) {
        setActiveId(String(active.id));
    }

    /**
     * Handles the end of a drag operation and updates item positions
     */
    function handleDragEnd({ active, over }: DragEndEvent) {
        setActiveId(undefined);

        if (active.id !== over?.id) {
            setItems((items) => {
                const ids = items.map((item) => item.id);
                const oldIndex = ids.indexOf(String(active.id));
                const newIndex = ids.indexOf(String(over?.id));
                return arrayMove(items, oldIndex, newIndex);
            });
        }
    }

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
            onDragStart={handleDragStart}
            modifiers={[restrictMovement]}
        >
            <SortableContext
                items={items}
                strategy={horizontal ? horizontalListSortingStrategy : verticalListSortingStrategy}
            >
                {children
                    ? children({ items })
                    : items.map((item, index) => (
                        <SortableItem
                            key={item.id}
                            id={item.id}
                            className={
                                activeId !== null && activeId === item.id
                                    ? "sortable-item sortable-item-dragging"
                                    : "sortable-item"
                            }
                        >
                            {itemRender?.({ item, index })}
                        </SortableItem>
                    ))}
            </SortableContext>
        </DndContext>
    );
}
